/**
 * theme.js — Main entry point
 * IntersectionObserver for scroll animations, form interception for add-to-cart.
 */

(function () {
  'use strict';

  /* ── Scroll-triggered animations ──────────────────── */
  /* Batch-sort intersecting entries by spatial position (top, then left)
     and assign --stagger-index 0..N-1 within the batch — only when the
     element has not already set --stagger-index inline via Liquid.
     Gives a clean left-to-right, row-by-row wave for grids like the
     collection page, while preserving authored stagger on other sections. */
  function revealBatch(entries) {
    const visible = entries.filter((e) => e.isIntersecting);
    if (visible.length === 0) return [];

    const auto = visible.filter(
      (e) => !e.target.style.getPropertyValue('--stagger-index')
    );
    auto.sort((a, b) => {
      const ar = a.target.getBoundingClientRect();
      const br = b.target.getBoundingClientRect();
      if (Math.abs(ar.top - br.top) > 8) return ar.top - br.top;
      return ar.left - br.left;
    });
    auto.forEach((entry, i) => {
      entry.target.style.setProperty('--stagger-index', i);
    });

    return visible;
  }

  function initAnimations() {
    const motionOK = window.matchMedia(
      '(prefers-reduced-motion: no-preference)'
    ).matches;

    if (!motionOK) return;

    const observer = new IntersectionObserver(
      (entries) => {
        revealBatch(entries).forEach((entry) => {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    document
      .querySelectorAll('[data-animate]')
      .forEach((el) => observer.observe(el));
  }

  /* ── Add-to-cart form interception ────────────────── */
  function initCartFormInterception() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action$="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      const btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.setAttribute('aria-busy', 'true');
      }

      try {
        const variantId = form.querySelector('[name="id"]').value;
        const quantity = form.querySelector('[name="quantity"]')?.value ?? 1;

        if (window.cartDrawer) {
          await window.cartDrawer.addItem(variantId, parseInt(quantity, 10));
        }
      } catch (err) {
        console.error('Add to cart failed:', err);
      } finally {
        if (btn) {
          btn.removeAttribute('aria-busy');
        }
      }
    });
  }

  /* ── Re-observe after section rendering swaps ─────── */
  function observeNewAnimations() {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const animatable = node.querySelectorAll
            ? [
                ...(node.matches('[data-animate]') ? [node] : []),
                ...node.querySelectorAll('[data-animate]')
              ]
            : [];

          const motionOK = window.matchMedia(
            '(prefers-reduced-motion: no-preference)'
          ).matches;

          if (motionOK && animatable.length > 0) {
            const observer = new IntersectionObserver(
              (entries) => {
                revealBatch(entries).forEach((entry) => {
                  entry.target.classList.add('is-visible');
                  observer.unobserve(entry.target);
                });
              },
              { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
            );
            animatable.forEach((el) => observer.observe(el));
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /* ── Parallax background images ────────────────────── */
  /* Moves [data-parallax-image] at a fraction of scroll speed while its
     section is in view. Uses a single rAF loop + IntersectionObserver so
     offscreen sections cost nothing. Strength is subtle by design: the
     image wrapper is oversized (see animations.css) so edges never show. */
  function initParallax() {
    const motionOK = window.matchMedia(
      '(prefers-reduced-motion: no-preference)'
    ).matches;
    if (!motionOK) return;

    const sections = Array.from(
      document.querySelectorAll('[data-parallax-section]')
    );
    if (sections.length === 0) return;

    const active = new Set();
    const STRENGTH = 0.18; // 0 = none, 1 = moves at scroll speed

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) active.add(e.target);
          else active.delete(e.target);
        });
        if (active.size > 0) schedule();
      },
      { rootMargin: '100px 0px 100px 0px' }
    );
    sections.forEach((s) => io.observe(s));

    let ticking = false;
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    function update() {
      ticking = false;
      const vh = window.innerHeight;
      active.forEach((section) => {
        const rect = section.getBoundingClientRect();
        // progress: 0 when section top at viewport bottom, 1 when section bottom at viewport top
        const progress = (vh - rect.top) / (vh + rect.height);
        const clamped = Math.max(0, Math.min(1, progress));
        const travel = rect.height * STRENGTH;
        const y = (clamped - 0.5) * travel * -1;
        const wrapper = section.querySelector('[data-parallax-wrapper]');
        if (wrapper) wrapper.style.setProperty('--parallax-y', y.toFixed(2) + 'px');
      });
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
  }

  /* ── Init ──────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initAnimations();
    initCartFormInterception();
    observeNewAnimations();
    initParallax();
  });
})();
